import { createContext, useContext } from 'react'
import { en, type DictKey } from '@/locales/en'
import { zh } from '@/locales/zh'
import { LANGS, type Lang } from './lang'

const DICTS: Record<Lang, Record<DictKey, string>> = { en, zh }

/** ICU-less interpolation: replaces {key} placeholders with params. */
export function translate(
  key: DictKey,
  lang: Lang,
  params?: Record<string, string | number>,
): string {
  let text: string = DICTS[lang][key] ?? en[key] ?? key
  if (params) {
    for (const [name, value] of Object.entries(params)) {
      text = text.replaceAll(`{${name}}`, String(value))
    }
  }
  return text
}

export type DictParams = Record<string, string | number>

export interface I18n {
  lang: Lang
  /** Translate a dictionary key with optional {placeholder} params. */
  t: (key: DictKey, params?: DictParams) => string
  /** Prepend the active language prefix to an internal path (e.g. /zh/tools/zip). */
  path: (to: string) => string
  /** Switch UI language: re-navigates the current route and persists a cookie. */
  switchLang: (next: Lang) => void
}

export const I18nContext = createContext<I18n | null>(null)

export function useI18n(): I18n {
  const value = useContext(I18nContext)
  if (!value) {
    throw new Error('useI18n must be used within <I18nProvider>')
  }
  return value
}

export { LANGS }
export type { Lang }